import React from 'react';
import Modal from 'react-bootstrap/Modal';
import Alert from 'react-bootstrap/Alert';
import { DistanceType } from '../../enums/DistanceType';
import { DataSource } from '../../enums/DataSource';
import { RecipeType } from '../../enums/RecipeType';
import { SeparationType } from '../../enums/SeparationType';
import { StatePrescriptionIdentification } from '../../enums/StatePrescriptionIdentification';
import { ProfileType } from '../../enums/ProfileType';
import { strings } from '../../resources/strings';
import { Recipe } from '../../models/Recipe';
import { MultifocalInfo } from '../../models/MultifocalInfo';
import { DiagnosticInformation } from '../../models/DiagnosticInformation';
import { SaveDiagnosticInformationInputBase } from '../../models/SaveDiagnosticInformationInputBase';
import { Frame } from '../../models/Frame';
import { CustomerRecipe } from '../../models/CustomerRecipe';
import { useAuth } from '../../contexts/AuthContext';
import ServicesContext from '../../contexts/ServicesContext';
import { SaveRecipeInput } from '../../services/Recipes/SaveRecipe/SaveRecipeInput';
import { SaveStatePrescriptionLogInput } from '../../services/StatesPrescriptions/SaveStatePrescriptionLog/SaveStatePrescriptionLogInput';
import { Button } from '../../common-components/Button/Button';
import { WaitingIcon } from '../../common-components/WaitingIcon/WaitingIcon';
import { FormRecipe, FormRecipeValues } from './FormRecipe/FormRecipe';
import { CardRecipe } from './CardRecipe/CardRecipe';
import { CustomerRecipeList } from './CustomerRecipeList/CustomerRecipeList';
import { FooterContent } from './styles';

interface IModalRecipesProps {
  visible: boolean;
  readonly?: boolean;
  close: (orderId?: number) => void;
  title: string;
  orderId?: number;
  orderNumber?: string;
  orderDataSource?: DataSource;
  orderIsPhysicalStore: boolean;
  customerId?: number;
}

const ModalRecipes: React.FC<IModalRecipesProps> = ({
  visible,
  readonly,
  close,
  title,
  orderId,
  orderNumber,
  orderDataSource,
  orderIsPhysicalStore,
  customerId
}) => {

  const { session } = useAuth();
  const [recipes, setRecipes] = React.useState<Recipe[]>([]);
  const [formRecipeVisible, setFormRecipeVisible] = React.useState<boolean>(false);
  const [formRecipeValues, setFormRecipeValues] = React.useState<FormRecipeValues | undefined>(undefined);
  const [frames, setFrames] = React.useState<Frame[]>([]);
  const [customerRecipeHistory, setCustomerRecipeHistory] = React.useState<CustomerRecipe[]>([]);
  const [customerRecipeListVisible, setCustomerRecipeListVisible] = React.useState<boolean>(false);
  const [loading, setLoading] = React.useState<boolean>(false);

  const {
    saveRecipeService,
    getRecipesByOrderService,
    updateRecipeService,
    deleteRecipeService,
    alertService,
    toastService,
    getFramesByOrderService,
    getCurrentStatesByOrdersService,
    saveStatePrescriptionLogService,
    getCustomerRecipeHistoryByIdService,
    linkRecipeWithCustomerRecipeService
  } = React.useContext(ServicesContext);

  React.useEffect(() => {
    if (!visible) return;
    getRecipesAndDependencies();
  }, [visible]);

  const memoBody = React.useMemo(() => {
    if (!visible) return null;

    if (loading) {
      return <WaitingIcon />;
    }

    if (customerRecipeListVisible) {
      return (
        <CustomerRecipeList
          customerId={customerId}
          linkRecipe={(value) => linkCustomerRecipe(value)}
        />
      )
    }

    if (formRecipeVisible) {
      return (
        <FormRecipe
          orderId={orderId}
          orderNumber={orderNumber}
          values={formRecipeValues}
          change={(values) => changeFormRecipeValues(values)}
          session={session}
          frames={frames}
          orderIsPhysicalStore={orderIsPhysicalStore}
        />
      );
    }

    const orderHasAnyRecipe = recipes?.length > 0;

    if (!orderHasAnyRecipe) {
      return (
        <Alert variant='warning'>
          {strings.NoneRecipeLinkedToOrder}
        </Alert>
      );
    }

    return recipes.map(recipe => {
      return (
        <CardRecipe
          key={`recipe-${recipe.id}`}
          recipe={recipe}
          editRecipe={() => editRecipe(recipe)}
          deleteRecipe={() => deleteRecipe(recipe)}
          orderDataSource={orderDataSource}
          readonly={readonly}
        />
      );
    });
  }, [
    visible, formRecipeVisible, formRecipeValues, orderId, orderNumber, recipes, readonly,
    session, frames, orderIsPhysicalStore, customerRecipeHistory, customerRecipeListVisible,
    customerId, loading
  ]);

  const memoFooter = React.useMemo(() => {
    if (!visible || readonly || loading) return null;

    if (customerRecipeListVisible) {
      return (
        <>
          <Button text={strings.Cancel} width='190px' marginRight='20px' click={() => cancelCustomerRecipeList()} />
        </>
      );
    }

    if (formRecipeVisible) {
      return (
        <>
          <Button text={strings.Cancel} width='190px' marginRight='20px' click={() => cancelFormRecipe()} />
          <Button text={strings.SaveRecipe} width='190px' click={() => saveFormRecipe()} />
        </>
      );
    }

    return (
      <>
        {customerRecipeHistory && customerRecipeHistory.length > 0 ? (
          <Button text={strings.UseRegisteredRecipe} width='190px' marginRight='20px' click={() => listCustomerRecipeHistory()} />
        ) : null}
        {session?.user?.profileType === ProfileType.Viewer ? null : (
          <Button text={strings.NewRecipe} width='190px' click={() => newRecipe()} />
        )}
      </>
    );
  }, [
    visible, formRecipeVisible, formRecipeValues, orderId, orderNumber, recipes, readonly,
    orderIsPhysicalStore, customerRecipeHistory, customerRecipeListVisible,
    customerId, loading, session
  ]);

  if (!visible) return null;

  return (
    <Modal
      size="lg"
      show={visible}
      onHide={() => clickCloseModal()}
      backdrop="static"
      keyboard={false}
    >
      <Modal.Header closeButton style={{ padding: '10px 1rem', height: '50px' }}>
        <Modal.Title>
          {title}
        </Modal.Title>
      </Modal.Header>

      <Modal.Body id='modalRecipeBody' style={{ maxHeight: '70vh', overflowY: 'auto', padding: '10px 1rem' }}>
        {memoBody}
      </Modal.Body>

      <Modal.Footer>
        <FooterContent>
          {memoFooter}
        </FooterContent>
      </Modal.Footer>
    </Modal>
  );

  function getRecipesAndDependencies() {
    getRecipes();
    getFrames();
    getCustomerRecipeHistory();
  }

  function getRecipes(checkCurrentStatePrescription?: boolean) {
    if (!orderId) return;
    setLoading(true);
    getRecipesByOrderService.handle({ orderId: orderId })
      .then(result => {
        const recipeList = result?.value?.recipes || [];
        setRecipes(recipeList);
        if (checkCurrentStatePrescription) handleCurrentStatePrescriptionAfterSaveRecipes();
      })
      .catch(() => {
        toastService.warning(strings.OpsSomethingWrongGettingTheData);
      })
      .finally(() => {
        setLoading(false);
      });
  }

  function handleCurrentStatePrescriptionAfterSaveRecipes() {
    if (!orderId) return;
    getCurrentStatesByOrdersService.handle({ ordersId: [orderId] })
      .then(result => {
        const currentStates = result?.value?.currentStates?.filter(cs => cs.orderId === orderId && cs.separationType === SeparationType.Prescription) || [];
        if (currentStates.length !== 1) return;
        const currentState = currentStates[0];
        if (currentState.isCancelled || currentState.isClosed || currentState.currentStatePrescriptionLogId) return;
        console.log('Inserting state prescription for order with current state: ', currentState);
        const input = new SaveStatePrescriptionLogInput();
        input.orderId = orderId;
        input.newStatePrescriptionId = StatePrescriptionIdentification.ReceitaPendente;
        saveStatePrescriptionLogService.handle(input);
      });
  }

  function getFrames() {
    if (!orderId) return;
    getFramesByOrderService.handle({ orderId: orderId })
      .then(result => setFrames(result?.value?.frames || []));
  }

  function clickCloseModal() {
    setFormRecipeVisible(false);
    setCustomerRecipeListVisible(false);
    close(orderId);
  }

  function newRecipe() {
    setFormRecipeVisible(true);
    setFormRecipeValues(undefined);
  }

  function changeFormRecipeValues(values: FormRecipeValues) {
    setFormRecipeValues(values);
  }

  function editRecipe(recipe: Recipe) {
    const newFormRecipeValues = new FormRecipeValues();
    newFormRecipeValues.id = recipe.id;
    newFormRecipeValues.name = recipe.customerName;
    newFormRecipeValues.pathToFile = recipe.pathToFile;
    newFormRecipeValues.recipeApproved = recipe.recipeApproved;
    newFormRecipeValues.nasoPupillaryDistanceApproved = recipe.nasoPupillaryDistanceApproved;
    newFormRecipeValues.nearDiagnostic = recipe.diagnosticsInformations?.find(dix => dix.distanceType === DistanceType.Near);
    newFormRecipeValues.farDiagnostic = recipe.diagnosticsInformations?.find(dix => dix.distanceType === DistanceType.Far);
    newFormRecipeValues.multifocalInfo = recipe.multifocalInfo;
    newFormRecipeValues.recipeType = recipe?.recipeType || RecipeType.SingleVision;
    newFormRecipeValues.multifocalInfoIsConfirmed = true;
    newFormRecipeValues.leftEyeHeight = recipe.leftEyeHeight;
    newFormRecipeValues.rightEyeHeight = recipe.rightEyeHeight;
    setFormRecipeVisible(true);
    setFormRecipeValues(newFormRecipeValues);
  }

  function cancelFormRecipe() {
    setFormRecipeVisible(false);
    setFormRecipeValues(undefined);
  }

  function formRecipeValuesIsValid() {
    if (!formRecipeValues) return false;
    if (
      formRecipeValues?.pathToFile ||
      formRecipeValues?.farDiagnostic?.leftSpherical || formRecipeValues?.farDiagnostic?.leftCylindrical ||
      formRecipeValues?.farDiagnostic?.leftAxis || formRecipeValues?.farDiagnostic?.leftNasoPupillaryDistance ||
      formRecipeValues?.farDiagnostic?.rightSpherical || formRecipeValues?.farDiagnostic?.rightCylindrical ||
      formRecipeValues?.farDiagnostic?.rightAxis || formRecipeValues?.farDiagnostic?.rightNasoPupillaryDistance ||
      formRecipeValues?.nearDiagnostic?.leftSpherical || formRecipeValues?.nearDiagnostic?.leftCylindrical ||
      formRecipeValues?.nearDiagnostic?.leftAxis || formRecipeValues?.nearDiagnostic?.leftNasoPupillaryDistance ||
      formRecipeValues?.nearDiagnostic?.rightSpherical || formRecipeValues?.nearDiagnostic?.rightCylindrical ||
      formRecipeValues?.nearDiagnostic?.rightAxis || formRecipeValues?.nearDiagnostic?.rightNasoPupillaryDistance
    ) return true;
    return false;
  }

  function formRecipeHeightsIsValid() {
    const leftEyeHeight = formRecipeValues?.leftEyeHeight ? +formRecipeValues.leftEyeHeight : null;
    const rightEyeHeight = formRecipeValues?.rightEyeHeight ? +formRecipeValues.rightEyeHeight : null;

    const allHeightsAreNull = !leftEyeHeight && !rightEyeHeight;
    if (allHeightsAreNull) return true;

    const informedLeftEyeHeightIsInvalid = leftEyeHeight && (leftEyeHeight < 0 || leftEyeHeight > 99);
    if (informedLeftEyeHeightIsInvalid) {
      toastService.warning(strings.ConfirmIfInformedFieldValueIsCorrect('Altura Olho Esquerdo'));
      return false;
    }

    const informedRightEyeHeightIsInvalid = rightEyeHeight && (rightEyeHeight < 0 || rightEyeHeight > 99);
    if (informedRightEyeHeightIsInvalid) {
      toastService.warning(strings.ConfirmIfInformedFieldValueIsCorrect('Altura Olho Direito'));
      return false;
    }

    return true;
  }

  async function saveFormRecipe() {
    if (!orderId) {
      toastService.warning(strings.UnidentifiedOrderToSaveTheRecipeYouNeedToIdentifyTheOrder);
      return;
    }
    if (!formRecipeValuesIsValid()) {
      alertService.warning(strings.ToSaveTheRecipeItIsNecessaryToAttachTheRecipeFileOrInformTheDiagnostic);
      return;
    }
    if (!formRecipeHeightsIsValid()) {
      return;
    }

    const input = await createSaveInput();

    if (!input) return;

    if (input.recipeId) {
      updateRecipeService.handle(input)
        .then(result => {
          if (!result?.value?.updateRecipeResult) return;
          setFormRecipeVisible(false);
          setFormRecipeValues(undefined);
          getRecipes(true);
        })
        .catch(() => {
          toastService.warning(strings.OpsSomethingWrongSendTheData);
        });
      return;
    }

    saveRecipeService.handle(input)
      .then(result => {
        if (!result?.value?.saveRecipeResult) return;
        setFormRecipeVisible(false);
        setFormRecipeValues(undefined);
        getRecipes(true);
      })
      .catch(() => {
        toastService.warning(strings.OpsSomethingWrongSendTheData);
      });
  }

  async function createSaveInput(): Promise<SaveRecipeInput | null> {
    return new Promise(async (resolve, reject) => {
      if (!orderId) {
        resolve(null);
        return;
      }

      const confirmation = getMultifocalInfoConfirmation();
      if (!confirmation) {
        alertService.warning('Antes de continuarmos, precisa verificar os dados da aba Multifocal.');
        return;
      }

      const input = new SaveRecipeInput();
      input.orderId = orderId;
      input.recipeId = formRecipeValues?.id;
      input.customerName = formRecipeValues?.name || '';
      input.customerEmail = '';
      input.pathToFile = formRecipeValues?.pathToFile || '';
      input.recipeIsApproved = orderIsPhysicalStore ? true : formRecipeValues?.recipeApproved || false;
      input.nasoPupillaryDistanceIsApproved = orderIsPhysicalStore ? true : formRecipeValues?.nasoPupillaryDistanceApproved || false;
      input.userId = session?.user?.id;
      input.diagnosticInformations = [];
      input.multifocalInfo = formRecipeValues?.multifocalInfo;
      input.recipeType = formRecipeValues?.recipeType ? +formRecipeValues.recipeType : RecipeType.SingleVision;
      input.leftEyeHeight = formRecipeValues?.leftEyeHeight ? +formRecipeValues.leftEyeHeight : null;
      input.rightEyeHeight = formRecipeValues?.rightEyeHeight ? +formRecipeValues.rightEyeHeight : null;

      let farDiagnostic: SaveDiagnosticInformationInputBase | null = null;
      let nearDiagnostic: SaveDiagnosticInformationInputBase | null = null;

      if (formRecipeValues?.farDiagnostic) {
        if (!formRecipeValues.farDiagnostic.id) {
          formRecipeValues.farDiagnostic.createByName = session?.user?.name || '';
        }
        formRecipeValues.farDiagnostic.updateByName = session?.user?.name || '';
        formRecipeValues.farDiagnostic.diagnosticInformationId = formRecipeValues.farDiagnostic.id;
        formRecipeValues.farDiagnostic.distanceType = DistanceType.Far;
        input.diagnosticInformations.push({ ...formRecipeValues.farDiagnostic });
        farDiagnostic = formRecipeValues.farDiagnostic;
      }

      if (formRecipeValues?.nearDiagnostic) {
        if (!formRecipeValues.nearDiagnostic.id) {
          formRecipeValues.nearDiagnostic.createByName = session?.user?.name || '';
        }
        formRecipeValues.nearDiagnostic.updateByName = session?.user?.name || '';
        formRecipeValues.nearDiagnostic.diagnosticInformationId = formRecipeValues.nearDiagnostic.id;
        formRecipeValues.nearDiagnostic.distanceType = DistanceType.Near;
        input.diagnosticInformations.push({ ...formRecipeValues.nearDiagnostic });
        nearDiagnostic = formRecipeValues.nearDiagnostic;
      }

      if (input.recipeType === RecipeType.Bifocal && (farDiagnostic || nearDiagnostic)) {
        if (!nearDiagnosticBeenProvided(nearDiagnostic)) {
          const alertResult = await alertService
            .confirm('O diagnóstico para perto não foi fornecido. Posso inferir a partir do diagnóstico para longe?');

          if (alertResult.isConfirmed) {
            nearDiagnostic = getInferredNearDiagnosticFromFar(farDiagnostic, nearDiagnostic, input.multifocalInfo);

            const confirmResult = await alertService.confirmHtml(getDiagnosticConfirmationMessage(farDiagnostic, nearDiagnostic));
            if (confirmResult.isDenied) {
              resolve(null);
              return;
            }

            input.diagnosticInformations = input.diagnosticInformations.filter(x => x.distanceType === DistanceType.Far);
            input.diagnosticInformations.push(nearDiagnostic);
          }
        }
      }

      resolve(input);
    });
  }

  function getMultifocalInfoConfirmation() {
    const recipeType = formRecipeValues?.recipeType ? +formRecipeValues.recipeType : RecipeType.SingleVision;

    if (recipeType === RecipeType.SingleVision) return true;

    return !!formRecipeValues?.multifocalInfoIsConfirmed;
  }

  function nearDiagnosticBeenProvided(nearDiagnostic?: SaveDiagnosticInformationInputBase | null) {
    return nearDiagnostic?.leftSpherical || nearDiagnostic?.leftCylindrical ||
      nearDiagnostic?.leftAxis || nearDiagnostic?.leftNasoPupillaryDistance ||
      nearDiagnostic?.rightSpherical || nearDiagnostic?.rightCylindrical ||
      nearDiagnostic?.rightAxis || nearDiagnostic?.rightNasoPupillaryDistance;
  }

  async function deleteRecipe(recipe: Recipe) {
    const recipeId = recipe.id;

    if (!recipeId) return;

    const confirmation = await alertService.confirm(strings.DoYouReallyWantToDeleteTheRecipe);

    if (!confirmation.isConfirmed) return;

    deleteRecipeService.handle({ recipeId: recipeId })
      .then(result => {
        setFormRecipeVisible(false);
        setFormRecipeValues(undefined);
        getRecipes();
      })
      .catch(() => {
        toastService.warning(strings.OpsSomethingWrongSendTheData);
      });
  }

  function getInferredNearDiagnosticFromFar(
    farDiagnostic?: SaveDiagnosticInformationInputBase | null, nearDiagnostic?: SaveDiagnosticInformationInputBase | null,
    multifocalInfo?: MultifocalInfo
  ): SaveDiagnosticInformationInputBase {
    let value: number | null | undefined = 0;
    const inferredNearDiagnostic = nearDiagnostic ? { ...nearDiagnostic } : new DiagnosticInformation();
    const addField = getDiagnosticDecimalValue(multifocalInfo?.addField);
    const defaultValueForCalibratedNpdInference = -2;

    value = farDiagnostic?.rightSpherical ? getDiagnosticDecimalValue(farDiagnostic?.rightSpherical) + addField : null;
    inferredNearDiagnostic.rightSpherical = getFormattedDiagnosticValue(value);
    inferredNearDiagnostic.rightCylindrical = farDiagnostic?.rightCylindrical;
    inferredNearDiagnostic.rightAxis = farDiagnostic?.rightAxis;
    value = farDiagnostic?.rightNasoPupillaryDistance
      ? getDiagnosticDecimalValue(farDiagnostic?.rightNasoPupillaryDistance) + defaultValueForCalibratedNpdInference
      : null;
    inferredNearDiagnostic.rightNasoPupillaryDistance = getFormattedDiagnosticValue(value);

    value = farDiagnostic?.leftSpherical ? getDiagnosticDecimalValue(farDiagnostic?.leftSpherical) + addField : null;
    inferredNearDiagnostic.leftSpherical = getFormattedDiagnosticValue(value);
    inferredNearDiagnostic.leftCylindrical = farDiagnostic?.leftCylindrical;
    inferredNearDiagnostic.leftAxis = farDiagnostic?.leftAxis;
    value = farDiagnostic?.leftNasoPupillaryDistance
      ? getDiagnosticDecimalValue(farDiagnostic?.leftNasoPupillaryDistance) + defaultValueForCalibratedNpdInference
      : null;
    inferredNearDiagnostic.leftNasoPupillaryDistance = getFormattedDiagnosticValue(value);

    inferredNearDiagnostic.distanceType = DistanceType.Near;

    return inferredNearDiagnostic;
  }

  function getDiagnosticDecimalValue(strintValue?: string | null): number {
    return strintValue ? +strintValue.replaceAll(',', '.') : 0;
  }

  function getFormattedDiagnosticValue(decimalValue?: number | null): string {
    return decimalValue !== undefined && decimalValue !== null ? decimalValue.toFixed(2).toString().replaceAll('.', ',') : '';
  }

  function getDiagnosticConfirmationMessage(farDiagnostic?: SaveDiagnosticInformationInputBase | null, nearDiagnostic?: SaveDiagnosticInformationInputBase | null) {
    const thFixed = (content: string) => `<th style="width:100px;min-width:100px;">${content}</th>`;
    const th = (content: string) => `<th style="width:25%;">${content}</th>`;
    const td = (content: string) => `<td style="border:1px solid #ddd;">${content}</td>`;
    return `
            <table style="width:100%;">
                <thead>
                    <tr>
                        ${thFixed('LONGE')}
                        ${th('ESF')}
                        ${th('CIL')}
                        ${th('EIXO')}
                        ${th('DNP')}
                    </tr>
                </thead>
                <tbody>
                    <tr>
                        ${td('OD')}
                        ${td(farDiagnostic?.rightSpherical || '')}
                        ${td(farDiagnostic?.rightCylindrical || '')}
                        ${td(farDiagnostic?.rightAxis || '')}
                        ${td(farDiagnostic?.rightNasoPupillaryDistance || '')}
                    </tr>
                    <tr>
                        ${td('OE')}
                        ${td(farDiagnostic?.leftSpherical || '')}
                        ${td(farDiagnostic?.leftCylindrical || '')}
                        ${td(farDiagnostic?.leftAxis || '')}
                        ${td(farDiagnostic?.leftNasoPupillaryDistance || '')}
                    </tr>
                </tbody>
            </table>
            <br/>
            <table style="width:100%;">
                <thead>
                    <tr>
                        ${thFixed('PERTO')}
                        ${th('ESF')}
                        ${th('CIL')}
                        ${th('EIXO')}
                        ${th('DNP')}
                    </tr>
                </thead>
                <tbody>
                    <tr>
                        ${td('OD')}
                        ${td(nearDiagnostic?.rightSpherical || '')}
                        ${td(nearDiagnostic?.rightCylindrical || '')}
                        ${td(nearDiagnostic?.rightAxis || '')}
                        ${td(nearDiagnostic?.rightNasoPupillaryDistance || '')}
                    </tr>
                    <tr>
                        ${td('OE')}
                        ${td(nearDiagnostic?.leftSpherical || '')}
                        ${td(nearDiagnostic?.leftCylindrical || '')}
                        ${td(nearDiagnostic?.leftAxis || '')}
                        ${td(nearDiagnostic?.leftNasoPupillaryDistance || '')}
                    </tr>
                </tbody>
            </table>
            <br/>
            <p><strong>Confirma o diagnóstico acima?</strong></p>
            `;
  }

  function getCustomerRecipeHistory() {
    if (!customerId) return;

    getCustomerRecipeHistoryByIdService.handle({customerId})
      .then(result => {
        setCustomerRecipeHistory(result?.value?.recipes || []);
      });
  }

  function cancelCustomerRecipeList() {
    setCustomerRecipeListVisible(false);
  }

  function listCustomerRecipeHistory() {
    setCustomerRecipeListVisible(true);
  }

  async function linkCustomerRecipe(customerRecipe: CustomerRecipe) {
    if (!customerRecipe || !orderId) return;

    setLoading(true);

    cancelCustomerRecipeList();

    linkRecipeWithCustomerRecipeService.handle({
      orderId: orderId,
      customerRecipeId: customerRecipe.id
    }).then(result => {
      getRecipesAndDependencies();
    }).catch(() => {
      toastService.warning(strings.OpsSomethingWrongSendTheData);
    }).finally(() => {
      setLoading(false);
    });
  }
}

export { ModalRecipes };